import { App, AppActions, AppLoader, AuthConfig, AuthToken, RemoveListenerFn, IUser } from '@nab/x-types';
import { AuthStrategy } from '@nab/x-auth-core';
import { LinkedAppAuthInitConfig, LinkedAppAuthError } from '../../types';

/**
 * [[include:packages/core/x-auth/src/authStrategies/LinkedAppAuthStrategy/README.md]]
 */

export abstract class LinkedAppAuthStrategy<A extends App = App, AUTH extends AuthConfig = AuthConfig> implements AuthStrategy {
    private linkedAppActions: AppActions = null;

    private loaderPromise: Promise<AppLoader<A, AUTH>> = null;

    private user: IUser;

    /**
     *
     * @param {LinkedAppAuthInitConfig} initConfig The initialisation configuration for linked App Auth Strategy
     * @example
     * ```typescript
     * const initConfig = {
     *   linkedAppConfig: {
     *     appMetaData: {
     *       id: 'miniapp-example',
     *       tag: 'miniapp-example',
     *       appType: 'XV2',
     *       assetManifestUrl: 'https://test.com/asset-manifest.json',
     *     },
     *   },
     *   getHostElement: () => Promise.resolve(`<Html Element reference>`), // This function can be used to find and refer any HTML element event if it is rendered via react DOM.
     *   successEventNames: ['user_authenticated'],
     *   failureEventNames: ['user_cancelled'],
     *   onSuccess: () => console.info('Login success'),
     *   onFailure: (e) => console.info('Login failure'),
     * }
     * ```
     */
    constructor(protected initConfig: LinkedAppAuthInitConfig<A>) {}

    public set LinkedAppAction(actionsInstance: AppActions) {
        this.linkedAppActions = actionsInstance;
    }

    /**
     * The property that will be used by the runtime (x-runtime / x-spa) to set the instances of the Shell actions once the runtime has been initialised.
     */
    public get LinkedAppAction(): AppActions {
        return this.linkedAppActions;
    }

    public set User(user: IUser) {
        this.user = user;
    }

    public get User(): IUser {
        return this.user;
    }

    /**
     * This function should return true if your auth strategy is capable of acquiring a token for the supplied config
     */
    public abstract supports(config: AUTH): boolean;

    /**
     * Validate actions is passed to linked app
     */
    protected validateLinkedAppAction() {
        if (!this.LinkedAppAction) {
            throw new Error(LinkedAppAuthError.MISSED_SHELL_ACTIONS);
        }
    }

    /**
     * Validate the getHostElement prop in initConfig
     * @param getHostElementFunc the function is used for getting hostElement
     */
    private async validateHostElement(getHostElementFunc) {
        if (!getHostElementFunc || typeof getHostElementFunc !== 'function') {
            throw new Error(LinkedAppAuthError.MISSED_GET_HOST_ELEMENT);
        } else {
            const hostElement = await getHostElementFunc();
            if (!hostElement || !(hostElement instanceof Element)) {
                throw new Error(LinkedAppAuthError.MISSED_GET_HOST_ELEMENT);
            }
        }
    }

    /**
     * The function used to define how the token is retrieved
     * Should be overridden in your auth strategy
     *
     * @param config the config supplied to this function will vary based on the auth and token type you require
     *
     * @example
     * ```typescript
     * interface IAppToken {
          // token object sent from the app, can include keys like below
     *    value: string;
     *    expiresInMilliseconds: number;
     * }
     * class SampleAuthStrategy<C extends DafAuthConfig = DafAuthConfig> extends LinkedAppAuthStrategy {
     *   public authenticate<AuthToken>(config: C): Promise<AuthToken> {
     *     if (config.flag?.loginType === 'external') {
             // checking whatever
     *     }
     *     const tokenFromApp = super.authenticate<IAppToken>(config);
     *     // converting the miniapp Based token to AuthToken or a derivative of AuthToken like DafAuthToken
     *     return {
              // Any key from where token value can read from
     *        tokenValue: tokenFromApp.value,
     *        id: uniqid(),
     *
              // Any key from where expiry is calculated.
              // It is important to return the expiry in milliseconds for cache token handling
     *        expires: Date.now() + tokenFromApp.expiresInMilliseconds,
     *        config
     *     } as unknown as AuthToken;
     *   }
     * }
     *
     * // Usage in miniapp
     * const tokenResponse = await authenticate({ flags: { loginType: 'external' } })
     * ```
     */
    public async authenticate<T = AuthToken<AUTH>>(authConfig: AUTH): Promise<T> {
        const { linkedAppConfig, successEventNames, failureEventNames, onFailure, onSuccess, getHostElement } = this.initConfig;

        this.validateLinkedAppAction();

        await this.validateHostElement(getHostElement);

        const eventHandlerRemovers: RemoveListenerFn[] = [];

        const promise = new Promise<T>((resolve, reject) => {
            const { addEventListener, embedMiniApp } = this.LinkedAppAction;
            const embedLinkedApp = async () => {
                try {
                    const hostElement = await getHostElement();

                    const linkedAppConfigWithHostElement = { ...linkedAppConfig, hostElement };

                    if (!this.loaderPromise) {
                        this.loaderPromise = embedMiniApp<A>(linkedAppConfigWithHostElement) as any;
                    }
                    await this.loaderPromise;
                } catch (e) {
                    reject(new Error(LinkedAppAuthError.LOAD_LINKED_APP_ERROR));
                }
            };

            embedLinkedApp();

            const unloadLinkedApp = async () => {
                try {
                    const loader = await this.loaderPromise;
                    await loader.unload();
                } catch (e) {
                    reject(new Error(LinkedAppAuthError.UNLOAD_LINKED_APP_ERROR));
                }
            };

            /**
             * This function will not require any return any value.
             * as it is used ti resolve the original promise
             * @param token: This is the token object returned by the linked app, that will be resolved with the id and config
             * @returns none, as it is used to resolve the original promise
             */
            const onSuccessEventHandler = async (token: T) => {
                await unloadLinkedApp();
                this.loaderPromise = null;
                if (token) {
                    if (onSuccess) {
                        onSuccess();
                    }
                    resolve(token);
                } else {
                    const error = new Error(LinkedAppAuthError.INVALID_TOKEN);
                    reject(error);
                }
            };
            const onFailureEventHandler = async (data: any) => {
                await unloadLinkedApp();
                this.loaderPromise = null;
                reject(data || new Error(LinkedAppAuthError.FAILURE_AUTH_PROCESS));
            };
            successEventNames.forEach((eventName) => {
                eventHandlerRemovers.push(addEventListener(eventName, onSuccessEventHandler));
            });

            failureEventNames.forEach((eventName) => {
                eventHandlerRemovers.push(addEventListener(eventName, onFailureEventHandler));
            });
        });

        try {
            return await promise;
        } catch (e) {
            if (onFailure) {
                onFailure(e);
            }
            this.loaderPromise = null;

            throw e;
        } finally {
            eventHandlerRemovers.forEach((r) => {
                if (typeof r !== 'undefined') {
                    r();
                }
            });
        }
    }
}
